import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { toast } from 'react-toastify';
import { confirmLeave } from './formGuard';

/**
 * Bouton Back matériel Android (APK Capacitor) — pile de navigation attendue :
 *  1. une surcouche est ouverte (modal, drawer, zoom d'image, dropdown) →
 *     on la ferme (Escape, qu'antd écoute nativement) ;
 *  2. un formulaire est modifié → confirmation avant de quitter (formGuard) ;
 *  3. il y a un historique → retour en arrière ;
 *  4. à la racine → double appui sous 2 s pour quitter l'application
 *     (jamais de fermeture brutale au premier appui).
 *
 * Sans effet sur le web : le listener n'est posé que sur plateforme native.
 */
const OVERLAY_SELECTOR = [
  '.ant-modal-wrap:not([style*="display: none"])',
  '.ant-drawer-open',
  '.ant-image-preview-wrap:not([style*="display: none"])',
  '.ant-select-dropdown:not(.ant-select-dropdown-hidden)',
  '.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)',
  // Popover contrôlé (ex. cloche de notifications) : présent = ouvert.
  '.ant-popover:not(.ant-popover-hidden)',
].join(', ');

let lastBackAt = 0;

export default function initAndroidBack() {
  if (!Capacitor.isNativePlatform()) return;

  CapApp.addListener('backButton', () => {
    // 1. Surcouche ouverte : Escape sur l'élément focalisé (antd ferme la
    //    surcouche la plus haute ; le focus est piégé dedans).
    const overlay = document.querySelector(OVERLAY_SELECTOR);
    if (overlay) {
      // Un popover antd ne piège pas le focus et ne se ferme PAS sur Escape : il
      // se ferme sur un clic EXTÉRIEUR. On simule donc un mousedown hors du popup
      // (ce que rc-trigger écoute) ; pour les autres surcouches, Escape suffit.
      if (overlay.classList.contains('ant-popover')) {
        document.body.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
        return;
      }
      const target = document.activeElement && document.activeElement !== document.body
        ? document.activeElement
        : overlay;
      target.dispatchEvent(new KeyboardEvent('keydown', {
        key: 'Escape', code: 'Escape', keyCode: 27, which: 27, bubbles: true, cancelable: true,
      }));
      return;
    }

    // 2. Formulaire modifié
    if (!confirmLeave()) return;

    // 3. Pile de navigation (les routeurs react-router data maintiennent idx)
    const idx = window.history.state?.idx ?? 0;
    if (idx > 0) {
      window.history.back();
      return;
    }

    // 4. Racine : double appui pour quitter
    const now = Date.now();
    if (now - lastBackAt < 2000) {
      CapApp.exitApp();
    } else {
      lastBackAt = now;
      toast.info('Appuyez encore sur Retour pour quitter', { autoClose: 1800 });
    }
  });
}
